import type { VettedPerson } from '../../services/talentCommunityApi';

/** Talent Search's filter rules and word match. Plain matching, sorted by name upstream: no score. */
export const FIELDS = ['Location', 'Seniority', 'Group', 'Consent scope', 'Source', 'Status'] as const;
export const OPS = ['Contains', 'Does not contain'] as const;

export interface Rule {
  id: number;
  field: (typeof FIELDS)[number];
  op: (typeof OPS)[number];
  value: string;
}

/** Words too common to search on. */
const STOP = new Set(['and', 'the', 'with', 'for', 'who', 'und', 'mit', 'der', 'die', 'das', 'oder', 'background', 'experience']);
export const searchWords = (q: string) =>
  q
    .toLowerCase()
    .split(/[^\p{L}\p{N}+#.]+/u)
    .filter((w) => w.length >= 3 && !STOP.has(w));

const haystack = (p: VettedPerson) => [p.name, p.role, p.stage, p.location, ...p.cvLines].filter(Boolean).join(' ').toLowerCase();

/** What a filter rule compares against, per field. */
function fieldValue(p: VettedPerson, field: Rule['field']): string {
  switch (field) {
    case 'Location':
      return p.location || '';
    case 'Seniority':
      return p.role || '';
    case 'Group':
      return 'Vetted Talent';
    case 'Consent scope':
      return p.news ? 'Scope A + B, job matches and news' : 'Scope A only, job matches';
    case 'Source':
      return 'Lever';
    case 'Status':
      return p.inProcess ? 'In a Lever process' : 'Available';
  }
}

/** Pure, so it can be tested: who a search and its rules let through, alphabetically. */
export function filterPeople(people: VettedPerson[], words: string[], rules: Rule[]): VettedPerson[] {
  return people.filter((p) => {
    const text = haystack(p);
    if (words.length && !words.some((w) => text.includes(w))) return false;
    return rules.every((r) => {
      const has = fieldValue(p, r.field).toLowerCase().includes(r.value.trim().toLowerCase());
      return r.op === 'Contains' ? has : !has;
    });
  });
}

